import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LoadingMessages } from "../../../utils/constants/guest-messages";
import { IPaginationPayload } from "../../../interfaces/shared/IPaginationPayload";
import { tryToFetchGuestsByRoom } from "../../../store/brokers/partner/guests/guestsSlice";
import { useGetGuestDetailsInfoMutation } from "../../../store/reservations/reservationsApi";
import GuestInfoModal from "./modals/GuestInfoModal";
import GuestsTable from "./tables/GuestsTable";
import ReadGuestEmails from "./read-emails";
import SelectCheckbox from "../../../shared/select-checkbox";
import Button from "../../../shared/button";
import Input from "../../../shared/input";

const tabs = [
    { name: "Future Reservations", value: "ongoing" },
    { name: "Past Stays", value: "completed" },
];

const guests = [
    { label: "All Guests", value: "All Guests" },
    { label: "VIP", value: "VIP" },
    { label: "Regular", value: "Regular" },
];

const Guests = () => {
    const navigation = useNavigate();
    const dispatch = useAppDispatch();
    const [rows, setRows] = useState<Array<any>>([]);
    const [activeTab, setActiveTab] = useState<any>(tabs[0]);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const [showGuestModal, setShowGuestModal] = useState<{
        show: boolean;
        data: any;
        reservationId?: string;
    }>();
    const [search, setSearch] = useState<any>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchGuest, setSearchGuest] = useState<any>();
    const [loadingText, setLoadingText] = useState(LoadingMessages[0]);
    const [selectedStatus, setSelectedStatus] = useState<any>(guests?.[0]);
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const paginationData = useAppSelector((state) => state?.guests?.guests?.data?.page);
    const guestsLoading = useAppSelector((state) => state.guests.guestsAreLoading);
    const state = useAppSelector((state) => state.guests);
    const [getGuestDetailsInfo, { isLoading: isLoadingGuestInfo }] = useGetGuestDetailsInfoMutation();

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        });
        searchParams.set('page', '1')
        searchParams.set('size', `${paginationState?.pageSize || 10}`)
        setSearchParams(searchParams)
    };

    useEffect(() => {
        const paginationStateFormat = paginationState
        if (searchParams?.get('page') && Number(searchParams?.get('page')) !== paginationState?.pageNumber) {
            paginationStateFormat.pageNumber = Number(searchParams?.get('page') || 1)
        }
        if (searchParams?.get('size')) {
            paginationStateFormat.pageSize = Number(searchParams.get('size') || 10)
        }
        if (paginationStateFormat !== paginationState) {
            setPaginationState(paginationStateFormat)
        }
        if (searchParams.get('search')) {
            setSearch(searchParams.get('search') || undefined)
        }
        if (searchParams.get('type') && searchParams?.get('type') !== activeTab?.value) {
            const findStatus = tabs?.find((item: any) => item?.value === searchParams.get('type'))
            setActiveTab(findStatus || tabs?.[0])
        }
        if (searchParams.get('status') && searchParams?.get('status') !== selectedStatus?.value) {
            const findStatus = guests?.find((item: any) => item?.value === searchParams.get('status'))
            setSelectedStatus(findStatus || tabs?.[0])
        }
    }, [
        searchParams.get('page'),
        searchParams.get('status'),
        searchParams.get('size'),
        searchParams.get('search'),
        searchParams.get('type'),
    ])

    const onClickShowGuestInfo = (data: any, reservationId?: string) => {
        setShowGuestModal({
            show: true,
            data: data,
            reservationId: reservationId,
        });
        document.body.style.overflow = "hidden";
    };

    useEffect(() => {
        if (!isLoadingGuestInfo) {
            setLoadingText(LoadingMessages[0]);
            return;
        }

        let index = 0;
        const interval = setInterval(() => {
            index = (index + 1) % LoadingMessages.length;
            setLoadingText(LoadingMessages[index]);
        }, 5000);

        return () => clearInterval(interval);
    }, [isLoadingGuestInfo]);

    const onChangeSearchGuestValue = (e: any) => {
        setSearchGuest(e);
    };

    const onChangeActiveTab = (tab: any) => {
        setLoadingData(true);
        setActiveTab(tab);
        searchParams.set('type', tab?.value)
        setSearchParams(searchParams)
        onResetPaginationState();
    };

    const onGettingFilters = () => {
        let data: any;
        if (activeTab) {
            if (activeTab?.value === "ongoing") {
                data = {
                    ...(data || {}),
                    ongoing: true,
                };
            } else {
                data = {
                    ...(data || {}),
                    completed: true,
                };
            }
        }
        if (selectedStatus && selectedStatus?.value !== "All Guests") {
            data = {
                ...(data || {}),
                guestStatus: selectedStatus?.value,
            };
        }
        if (search) {
            data = {
                ...(data || {}),
                search: search,
            };
        }
        const formatData: any = { data: data };
        return formatData;
    };

    const onGettingGuestsData = async () => {
        try {
            const data = onGettingFilters();
            const fetchData = {
                ...(paginationState || {}),
                ...(data || {}),
            };
            await dispatch(tryToFetchGuestsByRoom(fetchData)).unwrap();
        } catch (err) {
            // err
        }
        setLoadingData(false);
    };

    useEffect(() => {
        onGettingGuestsData();
    }, [paginationState]);

    useEffect(() => {
        if (state.guests) {
            const rows = state.guests?.data?.elements;
            setRows(rows);
        }
    }, [state.guests]);

    const onChangeSearchValue = (e: any) => {
        setSearch(e);
        if (e) searchParams.set('search', e)
        else searchParams.delete('search')
        searchParams.set('page', '1')
        setSearchParams(searchParams)
        onResetPaginationState();
    };

    const onCloseModal = () => {
        setShowGuestModal(undefined);
        document.body.style.overflow = "auto";
    };

    const onClickSearchProfile = async () => {
        if (searchGuest && searchGuest !== "") {
            try {
                const guestInfoData = {
                    name: searchGuest,
                };
                const response: any = await getGuestDetailsInfo(guestInfoData).unwrap();
                onClickShowGuestInfo(response?.data);
                setSearch(undefined);
            } catch (err) {
                // err here
            }
        }
    };

    const onClickDetails = (id: any) => {
        navigation(`/guest-intelligence/${id}`);
    };

    const onChangeSelectedStatus = (e: any) => {
        setSelectedStatus(e);
        searchParams.set('status', e?.value)
        setSearchParams(searchParams)
        onResetPaginationState();
    };

    return (
        <div>
            <div className="my-3">
                <p className="text-primary-light">Guests</p>
            </div>
            <div className="flex flex-wrap">
                <div className="w-full lg:w-8/12 xl:w-9/12 px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 my-3">
                        <SelectCheckbox
                            name="Guest Status"
                            placeholder="All Guests"
                            options={guests}
                            selectedOption={selectedStatus}
                            onChangeSelectedOption={onChangeSelectedStatus}
                        />
                    </div>
                    <div
                        className={`flex flex-col md:flex-row md:justify-between md:items-center bg-call-default mt-3 mb-10 p-1 !rounded`}
                    >
                        <div>
                            <Input
                                placeholder={"Search Guest Info Details"}
                                dataQa={"search"}
                                containerStyle="mb-0 pb-0 relative min-w-[400px]"
                                inputStyle={`mb-0 border-0 focus:outline-none`}
                                icon={"/assets/shared/search.svg"}
                                showValue={true}
                                inputValue={searchGuest}
                                disabled={!!isLoadingGuestInfo}
                                inputUniqueName={"search"}
                                onChangeInput={onChangeSearchGuestValue}
                                hideInputSpace={true}
                            />
                        </div>
                        <div className="flex flex-row justify-center md:justify-end my-3 md:my-0">
                            {isLoadingGuestInfo ? (
                                <p className="px-2 py-2 text-primary-default">{loadingText}</p>
                            ) : (
                                <Button
                                    label={
                                        isLoadingGuestInfo
                                            ? "Loading Data..."
                                            : "Search Guest Details"
                                    }
                                    className={`${!isLoadingGuestInfo
                                        ? "btn-primary-rounded-less bg-[#252B37]"
                                        : "btn-primary-rounded-less-disable cursor-not-allowed"
                                        } min-w-auto mr-3`}
                                    onClickButton={() => onClickSearchProfile()}
                                />
                            )}
                        </div>
                    </div>
                    <div className="bg-white !rounded-md py-1">
                        <div className="flex flex-col md:flex-row md:justify-between">
                            <div className="flex flex-row flex-wrap items-center my-2 px-2 md:px-0">
                                {tabs?.map((item: any, idx: number) => {
                                    return (
                                        <div
                                            key={idx}
                                            onClick={() => onChangeActiveTab(item)}
                                            className={`border border-[#00330F] rounded-full py-1 px-3 mr-3 cursor-pointer ${activeTab?.value === item?.value
                                                ? "bg-[#e7f7e5]"
                                                : "bg-white"
                                                }`}
                                        >
                                            <p
                                                className={
                                                    activeTab?.value === item?.value
                                                        ? "text-[#00330F] font-medium"
                                                        : "text-[#5D6679] font-medium"
                                                }
                                            >
                                                {item?.name}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="px-2 my-4 md:my-0 md:px-0">
                                <Input
                                    placeholder={"Search by room, guest or reservation"}
                                    dataQa={"search"}
                                    containerStyle="mb-0 pb-0 relative min-w-[340px] "
                                    inputStyle={`mb-0 border focus:outline-none !rounded-md`}
                                    icon={"/assets/shared/search.svg"}
                                    showValue={true}
                                    inputValue={search}
                                    inputUniqueName={"search"}
                                    onChangeInput={onChangeSearchValue}
                                    hideInputSpace={true}
                                />
                            </div>
                        </div>
                        <GuestsTable
                            rows={rows}
                            rowsLoading={guestsLoading || loadingData}
                            paginationData={paginationData}
                            setPaginationState={setPaginationState}
                            onClickDetails={onClickDetails}
                        />
                        {showGuestModal?.show && (
                            <GuestInfoModal
                                openModal={showGuestModal?.show}
                                data={showGuestModal?.data || ""}
                                reservationId={showGuestModal?.reservationId}
                                handleCloseModal={onCloseModal}
                            />
                        )}
                    </div>
                </div>
                <div className="w-full lg:w-4/12 xl:w-3/12 px-4">
                    <div className="bg-white !rounded-md py-1">
                        <ReadGuestEmails />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Guests;
